/**
 * AiTutorPage — Interactive AI-narrated course module delivery.
 *
 * Route: /tutor/:courseId/:moduleId?enrollmentId=...
 *
 * EPIC-08-11: Agents with ai_tutor_enabled modules land here instead of
 * the static ReadingPage. The AI delivers content section-by-section,
 * asks comprehension questions, and marks the module complete on the
 * final correct answer.
 */

import { useState, useRef, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import styles from './AiTutorPage.module.css';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface TutorState {
  current_section: number;
  total_sections: number;
  chat_history: ChatMessage[];
  status: 'in_progress' | 'completed';
}

interface TutorMessageResponse {
  role: 'assistant';
  content: string;
  section_advanced: boolean;
  module_completed: boolean;
}

export function AiTutorPage(): JSX.Element {
  const { t } = useTranslation('training');
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const enrollmentId = searchParams.get('enrollmentId') ?? '';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [totalSections, setTotalSections] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const tutorUrl = `/courses/${courseId}/modules/${moduleId}/tutor`;

  // Load initial state
  useEffect(() => {
    apiClient
      .get<{ data: TutorState }>(tutorUrl, { params: { enrollment_id: enrollmentId } })
      .then((res) => {
        const s = res.data.data;
        setMessages(s.chat_history);
        setCurrentSection(s.current_section);
        setTotalSections(s.total_sections);
        setIsComplete(s.status === 'completed');
      })
      .catch(() => setError(t('aiTutor.error.loadFailed')))
      .finally(() => setIsLoading(false));
  }, [tutorUrl, enrollmentId, t]);

  // Auto-scroll to bottom (not available in jsdom)
  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend(): Promise<void> {
    const content = inputValue.trim();
    if (!content || isSending || isComplete) return;

    setInputValue('');
    setMessages((prev) => [...prev, { role: 'user', content }]);
    setIsSending(true);

    try {
      const res = await apiClient.post<{ data: TutorMessageResponse }>(
        `${tutorUrl}/message`,
        { content },
        { params: { enrollment_id: enrollmentId } },
      );
      const resp = res.data.data;
      setMessages((prev) => [...prev, { role: 'assistant', content: resp.content }]);
      if (resp.section_advanced) setCurrentSection((s) => s + 1);
      if (resp.module_completed) setIsComplete(true);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: t('aiTutor.error.sendFailed') }]);
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  if (isLoading) {
    return (
      <main className={styles.page}>
        <p className={styles.loading}>{t('common.loading')}</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.page}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          ← {t('common.button.back')}
        </button>
        <p className={styles.errorMsg}>{error}</p>
      </main>
    );
  }

  const progress = totalSections > 0 ? Math.round((currentSection / totalSections) * 100) : 0;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          ← {t('common.button.back')}
        </button>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <span className={styles.progressLabel}>
          {t('aiTutor.progress', { current: currentSection, total: totalSections })}
        </span>
      </header>

      <section className={styles.chatArea} aria-label={t('aiTutor.chatLabel')}>
        {messages.map((msg, i) => (
          <div
            key={i}
            className={msg.role === 'assistant' ? styles.assistantBubble : styles.userBubble}
          >
            <div className={styles.bubbleContent}>{msg.content}</div>
          </div>
        ))}
        <div ref={bottomRef} />
      </section>

      {isComplete ? (
        <div className={styles.completionBanner}>
          <p>{t('aiTutor.moduleComplete')}</p>
          <button className={styles.doneBtn} onClick={() => navigate(-1)}>
            {t('aiTutor.backToCourse')}
          </button>
        </div>
      ) : (
        <footer className={styles.inputRow}>
          <textarea
            className={styles.input}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('aiTutor.inputPlaceholder')}
            rows={2}
            disabled={isSending}
            aria-label={t('aiTutor.inputLabel')}
          />
          <button
            className={styles.sendBtn}
            onClick={() => void handleSend()}
            disabled={isSending || !inputValue.trim()}
          >
            {isSending ? t('aiTutor.sending') : t('aiTutor.sendButton')}
          </button>
        </footer>
      )}
    </main>
  );
}
